import React, { useState } from 'react';
import { View, Text, TextInput, ActivityIndicator } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { isPhone, isWeb } from '@helpers';
import { scale as initialScale, notchTop, Element, scaleOP } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import Line from '@components/common/line';

import ColorsWrapper from '@styles/colors';
import { fontOP, font as initialFont } from '@styles/fonts';

import { feedback } from '@reducers/ticket';
import { modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';

const texts = {
  error: {
    header: 'Report to Smart Factory',
    placeholder: 'Describe your issue including how to reproduce it',
    caption:
      'When you submit feedback Smart Factory collects some basic information about your device and app that’s used to troubleshoot the problem',
  },
  bug: {
    header: 'Bug Report',
    placeholder: 'Describe your issue including how to reproduce it',
    caption:
      'When you submit feedback Smart Factory collects some basic information about your device and app that’s used to troubleshoot the problem',
  },
  design: {
    header: 'Design or Ease of Use',
    placeholder: 'Briefly describe your suggestion on how things could work better',
    caption: 'When you submit feedback Smart Factory collects some basic information about your device and app that’s used to improve this app',
  },
  feature: {
    header: 'Feature Request',
    placeholder: 'Briefly describe your suggestion on how things could work better',
    caption: 'When you submit feedback Smart Factory collects some basic information about your device and app that’s used to improve this app',
  },
  help: {
    header: 'Ask for Help',
    placeholder: 'Type your question...',
    caption: 'When you submit feedback Smart Factory collects some basic information about your device and app that’s used to improve this app',
  },
};

const FeebackMessageScreen = ({ C, hide, submitEnable, isSubmiting, setMessage, message, submit, type }) => {
  const { safeIsOperatorPanelInfoScreen } = useNavigationCheck();
  const scale = safeIsOperatorPanelInfoScreen ? scaleOP : initialScale;
  const font = safeIsOperatorPanelInfoScreen ? fontOP : initialFont;

  return (
    <View
      style={[
        {
          width: isPhone ? '100%' : scale(480),
          marginTop: isPhone ? scale(60) + notchTop : 0,
          backgroundColor: C('Background/Light/Panel'),
          borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
          borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
        },
        isPhone
          ? {
              height: '100%',
            }
          : {
              minHeight: scale(391),
              paddingBottom: scale(8),
              borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
            },
      ]}
    >
      <View style={{ width: '100%', height: scale(52) }}>
        <Element scale={scale} {...{ config: { zIndex: 1000 } }}>
          <TouchableElement onPress={hide}>
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  {
                    padding: scale(15),
                  },
                  boxStyle,
                ]}
              >
                <Text
                  style={[
                    {
                      color: C(pressHoverColor('System/Light/Blue')),
                    },
                    font.Body,
                  ]}
                >
                  {i18n.t('Cancel')}
                </Text>
              </View>
            )}
          </TouchableElement>
        </Element>
        <Element scale={scale} {...{ config: { zIndex: 1000, position: { right: 0 } } }}>
          <TouchableElement onPress={() => (submitEnable && !isSubmiting ? submit() : null)}>
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  {
                    padding: scale(15),
                  },
                  boxStyle,
                ]}
              >
                <Text
                  style={[
                    {
                      color: C(pressHoverColor(submitEnable ? 'Link/Light/Primary' : 'Label/Light/Quinary')),
                    },
                    font.Headline,
                  ]}
                >
                  {isSubmiting ? <ActivityIndicator size="small" color="#777777" /> : i18n.t('Submit')}
                </Text>
              </View>
            )}
          </TouchableElement>
        </Element>
        <View style={{ width: '100%', justifyContent: 'center', height: '100%', alignContent: 'center', alignItems: 'center' }}>
          <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t(texts[type].header)}</Text>
        </View>
      </View>
      <View
        style={{
          width: '100%',
          paddingLeft: scale(30),
          paddingRight: scale(30),
          marginTop: scale(23.5),
          justifyContent: 'space-between',
          flexDirection: 'row',
        }}
      >
        <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('DETAILS')}</Text>
        <Text style={[font.Footnote, { color: C('Label/Light/Tertiary'), fontVariant: ['tabular-nums'] }]}>{500 - message.length}</Text>
      </View>
      <View style={{ marginTop: scale(4), paddingLeft: scale(15), paddingRight: scale(15) }}>
        <TextInput
          style={[
            {
              backgroundColor: C(modalFieldBg),
              paddingLeft: scale(15),
              paddingRight: scale(15),
              paddingTop: scale(12),
              paddingBottom: scale(12),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              height: scale(240),
              color: C('Label/Light/Primary'),
            },
            isWeb && {
              border: 'none',
              outline: 'none',
            },
          ]}
          value={message}
          onChangeText={(t) => {
            setMessage(t);
          }}
          maxLength={500}
          multiline={true}
          underlineColorAndroid="transparent"
          placeholder={i18n.t(texts[type].placeholder)}
          placeholderTextColor={C('Label/Light/Quinary')}
          autoCorrect={false}
          textAlignVertical="top"
        />
      </View>
      <View
        style={{
          width: '100%',
          paddingLeft: scale(30),
          paddingRight: scale(30),
          marginTop: scale(5.5),
        }}
      >
        <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(texts[type].caption)}</Text>
      </View>
    </View>
  );
};

const FeedbackTypeScreen = ({ C, hide, setType }) => {
  const { safeIsOperatorPanelInfoScreen } = useNavigationCheck();
  const scale = safeIsOperatorPanelInfoScreen ? scaleOP : initialScale;
  const font = safeIsOperatorPanelInfoScreen ? fontOP : initialFont;

  const types = [
    {
      name: `Bug Report`,
      bottom: 'Something didn’t work',
      value: false,
      onClick: () => setType('bug'),
    },
    {
      name: `Design or Ease of Use`,
      bottom: 'Issues with usability',
      value: false,
      onClick: () => setType('design'),
    },
    {
      name: `Feature Request`,
      bottom: 'Ideas to improve the app',
      value: false,
      onClick: () => setType('feature'),
    },
  ];

  return (
    <View
      style={[
        {
          width: isPhone ? '100%' : scale(480),
          height: isPhone ? '100%' : scale(251),
          marginTop: isPhone ? scale(60) + notchTop : 0,
          backgroundColor: C('Background/Light/Panel'),
          borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
          borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
        },
        !isPhone && {
          borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
          borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
        },
      ]}
    >
      <View style={{ width: '100%', height: scale(52) }}>
        <Element scale={scale} {...{ config: { zIndex: 1000 } }}>
          <TouchableElement onPress={hide}>
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  {
                    padding: scale(15),
                  },
                  boxStyle,
                ]}
              >
                <Text
                  style={[
                    {
                      color: C(pressHoverColor('System/Light/Blue')),
                    },
                    font.Body,
                  ]}
                >
                  {i18n.t('Cancel')}
                </Text>
              </View>
            )}
          </TouchableElement>
        </Element>
        <View style={{ width: '100%', justifyContent: 'center', height: '100%', alignContent: 'center', alignItems: 'center' }}>
          <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('Submit Feedback')}</Text>
        </View>
        <View style={{ marginTop: scale(28), paddingLeft: scale(15), paddingRight: scale(15) }}>
          {types.map((i, index) => (
            <Line
              key={i.name}
              {...{
                supportTwoLine: true,
                item: i,
                index,
                array: types,
                bigSize: true,
                C,
                topBorder: index !== 0,
                roundTop: index === 0,
                roundBottom: index === types.length - 1,
                bg: modalFieldBg,
              }}
            />
          ))}
        </View>
      </View>
    </View>
  );
};

const FeedbackComponent = (props) => {
  const { C, hide, feedback, type, screen } = props;
  const [message, setMessage] = useState('');
  const [isSubmiting, setIsSubmiting] = useState(false);
  const submitEnable = Boolean(message.length);

  const { safeIsOperatorPanelInfoScreen } = useNavigationCheck();
  const scale = safeIsOperatorPanelInfoScreen ? scaleOP : initialScale;
  const font = safeIsOperatorPanelInfoScreen ? fontOP : initialFont;

  const submit = async () => {
    setIsSubmiting(true);
    await feedback({ type, message });
    setIsSubmiting(false);
    hide();
  };

  const commonProps = { ...props, submitEnable, isSubmiting, setMessage, message, submit };

  return (
    <View
      style={{
        position: 'absolute',
        zIndex: 1000000,
        width: '100%',
        height: '100%',
        backgroundColor: C('Overlay/Light/Dim strong'),
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
      }}
    >
      {screen === 'types' ? <FeedbackTypeScreen {...commonProps} /> : <FeebackMessageScreen {...commonProps} />}
    </View>
  );
};

const RenderComponent = connect(null, { feedback })(ColorsWrapper(FeedbackComponent));

class Feedback extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showFeedback: false,
      type: '',
      screen: '',
    };
  }

  show = (d = {}) => {
    const { type, screen } = d;
    this.setState({ showFeedback: true, type, screen: type ? screen || 'feedback' : 'types' });
  };

  hide = () => {
    this.setState({ showFeedback: false });
  };

  setType = (type) => {
    this.setState({ screen: 'feedback', type });
  };

  render() {
    if (!this.state.showFeedback) {
      return null;
    }
    return <RenderComponent {...{ ...this.state, hide: this.hide, setType: this.setType }} />;
  }
}

export default Feedback;
